/**
 * TUT-224 — as SUPERFÍCIES compartilhadas: o idioma que nasceu no Console
 * (TUT-156/160) deixa de ser dele. A decisão do dono (30/08): "o Console
 * precisa seguir a mesma metodologia... não precisa ser diferente, precisa
 * ser inteligente" — um sistema só, com DENSIDADES nomeadas em vez de dois
 * vocabulários de layout.
 *
 * As duas densidades da casa:
 * - CONFORTÁVEL: o respiro de 24px do `Panel variant="form"` (TUT-218/220),
 *   para telas de formulário e leitura do operacional.
 * - ADMINISTRATIVA (`corpoDePainelDensoClass`): apertada no telefone,
 *   confortável no desktop — ferramenta de operação para perfil técnico.
 *
 * `apps/web/src/pages/platform/console-surface.ts` é SHIM de re-export
 * destes nomes (os ~53 usos do Console não mudaram; zero pixel mudou na
 * extração), e a guarda `console-nao-diverge.test.ts` reprova classe
 * própria nascendo lá — a trava contra divergência que o dono pediu em
 * 06/08 virou mecânica.
 */

/** Corpo de painel na densidade ADMINISTRATIVA (ex-consolePanelBodyClass). */
export const corpoDePainelDensoClass = "grid gap-3 p-3 sm:gap-4 sm:p-4";

/**
 * Cartão com elevação e hover-lift (ex-consoleCardClass): repouso em
 * `--shadow-sm`, hover em `--shadow-md` com 1px de subida; anima só
 * `transform`/`box-shadow` (nunca layout), duração de `--transition`, e sob
 * `prefers-reduced-motion` a subida some e a elevação fica.
 */
// TUT-470 — o cartão e o herói passam ao vidro do redesenho (TUT-463), a
// pedido do dono (03/10: "o card de destaque precisa ficar no mesmo estilo
// do restante"): canto do vidro (24px), fundo `--vidro`, borda e sombra do
// vidro. O herói deixou de ser o `--brand-night` fixo nos dois temas: com o
// vidro, ele acompanha o tema como o resto, e a tinta é a de sempre
// (`--text`), já medida sobre o vidro em cada mancha.
export const cartaoElevadoClass =
  "rounded-[var(--radius-vidro)] border border-[color:var(--vidro-borda)] bg-[color:var(--vidro)] shadow-[var(--vidro-sombra)] [transition:box-shadow_var(--transition),transform_var(--transition)] hover:-translate-y-px hover:shadow-[var(--shadow-md)] motion-reduce:transform-none motion-reduce:transition-none";

/**
 * Cartão-herói (ex-consoleHeroClass): o destaque do topo do Console. Até a
 * TUT-470 era escuro em `--brand-night`; agora é o vidro, como o resto.
 */
export const cartaoHeroiClass =
  "rounded-[var(--radius-vidro)] border border-[color:var(--vidro-borda)] bg-[color:var(--vidro)] p-4 text-[var(--text)] shadow-[var(--vidro-sombra)] sm:p-5";

/**
 * TUT-470 — o fundo com MANCHAS da tela (TUT-467), compartilhado: o app do
 * escritório, o Console, o portal externo e as telas de entrada pintam a raiz
 * com ele. Fundo fixo na janela (`bg-fixed`), e não camada atrás de tudo: uma
 * camada pedia `isolate` na raiz e prendia avisos com z-index abaixo dos
 * portais (o "Desfazer" do Kanban, achado da TUT-467).
 */
export const fundoComManchasClass =
  "bg-fixed bg-[radial-gradient(ellipse_48vw_52vh_at_6%_0%,var(--mancha-2),transparent_70%),radial-gradient(ellipse_40vw_50vh_at_100%_58%,var(--mancha-1),transparent_70%),radial-gradient(ellipse_44vw_40vh_at_44%_100%,var(--mancha-3),transparent_70%)]";

/**
 * TUT-510 — a SUPERFÍCIE de vidro do Lumen, num nome só: o canto do vidro
 * (24px), a borda, o fundo e a sombra do vidro, SEM desfoque. É a moldura do
 * `Panel` e de quem imita o painel (o esqueleto de carregamento). Fora dos
 * primitivos, ninguém monta essas quatro classes à mão: a trava
 * `acabamento-leve.test.ts` (P34) reprova.
 */
export const superficieDeVidroClass =
  "rounded-[var(--radius-vidro)] border border-[color:var(--vidro-borda)] bg-[color:var(--vidro)] shadow-[var(--vidro-sombra)]";

/**
 * TUT-510 — a coluna de quadro em vidro (sem sombra: quem flutua é o cartão
 * por cima dela). Os dois quadros e o esqueleto do quadro a usam.
 */
export const vidroDaColunaClass =
  "rounded-[var(--radius-vidro)] border border-[color:var(--vidro-borda)] bg-[color:var(--vidro)]";

/**
 * TUT-510 (revisão 1) — o vidro do CARTÃO de quadro, irmão dos de cima: o
 * vidro forte com a borda e a sombra do vidro, no canto do bloco (16px), SEM
 * desfoque. Era escrito no `cartao-do-quadro` da web.
 */
export const vidroDoCartaoClass =
  "rounded-[var(--radius-bloco)] border border-[color:var(--vidro-borda)] bg-[color:var(--vidro-forte)] shadow-[var(--vidro-sombra)]";

/**
 * TUT-510 — a CAMADA de vidro do Lumen: o que sobe por cima da tela (diálogo,
 * gaveta, popover) é o vidro forte, com desfoque. É a ÚNICA superfície que
 * desfoca: cartões e tabelas seguem sem filtro de fundo, e a trava
 * `acabamento-leve.test.ts` (P34) reprova desfoque fora das camadas. O
 * substituto sólido (menos transparência pedida, ou navegador sem
 * filtro de fundo) mora nos tokens, no fim do `theme-brevi.css`: aqui não
 * há variante de mídia nenhuma, e por isso a receita é uma linha.
 */
export const camadaDeVidroClass =
  "border-[color:var(--vidro-borda)] bg-[color:var(--vidro-forte)] [backdrop-filter:var(--vidro-desfoque)]";

/**
 * TUT-563a (Lumen 2.0) — o BOTÃO DE VIDRO, a receita aprovada na prévia
 * (`docs/design/prototipo-botoes-lumen.html`): o brilho em degradê no topo
 * (no lugar do fio de 1 px), a superfície do tema a 45% (70% no hover), o
 * aro translúcido uniforme e a sombra difusa. Os valores de cada tema moram
 * nos tokens `--botao-brilho`, `--botao-aro` e `--botao-aro-fino`.
 *
 * SEM desfoque aqui: o desfoque do botão depende de ONDE ele está (decisão 24
 * do dono), e quem decide é o primitivo que o veste (`Button`, `IconButton`),
 * com a exceção do `data-sem-desfoque` (P34).
 */
export const botaoDeVidroClass =
  "border-[color:var(--botao-aro)] text-foreground " +
  "[background:linear-gradient(180deg,var(--botao-brilho),transparent_55%),color-mix(in_srgb,var(--surface)_45%,transparent)] " +
  "hover:[background:linear-gradient(180deg,var(--botao-brilho),transparent_55%),color-mix(in_srgb,var(--surface)_70%,transparent)] " +
  "shadow-[0_0_0_0.5px_var(--botao-aro-fino),0_6px_18px_-8px_rgb(15_30_46/0.25)]";

/**
 * TUT-563a — o PRIMÁRIO do Lumen 2.0: o laranja translúcido (92%) com o
 * brilho branco no topo, o aro do próprio laranja e a sombra na cor dele. O
 * texto é o par do acento, medido a 4,5:1 sobre o laranja composto em cada
 * fundo (`contrast.test.ts`, risco 4 do plano).
 */
export const botaoPrimarioClass =
  "border-[color:color-mix(in_srgb,var(--accent)_70%,white)] text-accent-foreground " +
  "[background:linear-gradient(180deg,rgb(255_255_255/0.28),transparent_55%),color-mix(in_srgb,var(--accent)_92%,transparent)] " +
  "shadow-[0_8px_20px_-8px_color-mix(in_srgb,var(--accent)_60%,transparent)] hover:brightness-105";

/**
 * TUT-563a — o CONTORNO, o neutro dos lugares densos (célula, ficha do
 * processo, menu): o aro de 1 px, sem brilho, sem sombra e sem desfoque.
 */
export const botaoContornoClass =
  "border-border-strong bg-transparent text-foreground shadow-none hover:border-primary hover:bg-[var(--hover)]";

/**
 * TUT-510 — o controle da barra em PÍLULA (busca e filtros), sobre o vidro
 * forte e sem desfoque: o controle mora dentro de uma superfície que já é
 * vidro, e a pílula só precisa se destacar dela.
 */
export const controleEmPilulaClass =
  // A borda é a `--border` do campo, e não a do vidro (10% de alfa): o
  // contorno do campo precisa se ler sobre o vidro (revisão 1, M8).
  "rounded-full border-border bg-[color:var(--vidro-forte)]";

/**
 * TUT-511 — o ALTERNADOR em pílula do Lumen: o trilho. É o grupo de opções
 * que TROCA A LEITURA da tela (Mês | Semana | Dia, Lista | Kanban, Atendimento |
 * Chat), e não um campo de formulário: a pílula inteira sobre o vidro forte,
 * irmã da busca e do filtro da barra, com a mesma altura de controle (40 px).
 * As opções moram DENTRO dele (`opcaoDoAlternadorClass`). A fileira
 * retangular de bordas unidas é a `segmentada` do `GrupoDeEscolha`, que
 * continua sendo o campo de formulário e fica entre os outros campos.
 */
export const alternadorEmPilulaClass =
  // O canto é metade da altura de controle (`--control-h`/2 = 20 px): com UMA linha é a
  // pílula inteira, e com duas (oito modalidades de Viagens a 375 px) o canto
  // não cresce com a altura. Com `rounded-full` a trilha de duas linhas ficava
  // com raio de ~37 px e a opção do canto, de 16 px, vazava da curva (revisão
  // 1 da TUT-511). A opção está a ~3,4 px da borda: 20 − 3,4 ≈ 16, curvas
  // concêntricas. A prova é medida na e2e `tut-511-alternador-quebra.spec.ts`.
  controleEmPilulaClass.replace("rounded-full", "rounded-[calc(var(--control-h)/2)]") +
  " inline-flex min-h-[var(--control-h)] w-fit max-w-full flex-wrap items-stretch gap-[0.15rem] border p-[0.15rem]";

/**
 * TUT-511 — a OPÇÃO do alternador em pílula, num nome só para o botão
 * (`aria-pressed`), o link (`aria-current="page"`), a aba (`aria-selected`) e o
 * item do Radix (`data-state="on"`): a escolhida é preenchida com o par do
 * botão primário (TUT-472, medido no `contrast.test.ts`) e o resto da receita
 * é igual. `font-semibold` nos DOIS estados: peso que muda ao escolher faz as
 * opções dançarem a cada clique.
 */
export const opcaoDoAlternadorClass =
  "inline-flex min-h-[var(--control-h-compact)] min-w-8 cursor-pointer items-center justify-center gap-[0.35rem] whitespace-nowrap rounded-full border border-transparent bg-transparent px-3 text-corpo font-semibold text-[var(--text)] no-underline outline-0 " +
  "transition-[background,color,border-color,transform] duration-[var(--dur-fast)] hover:bg-[var(--hover)] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 " +
  "focus-visible:border-focus focus-visible:shadow-[0_0_0_3px_var(--focus-highlight)] " +
  "disabled:cursor-not-allowed disabled:text-[var(--muted)] disabled:hover:bg-transparent " +
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 " +
  // A escolhida: o seletor é um só, escrito uma vez por propriedade, e o
  // `hover` dela fixa o fundo (senão o `hover:bg` da base a apagaria).
  "[&:is([aria-pressed=true],[aria-selected=true],[aria-current=page],[data-state=on])]:border-primary [&:is([aria-pressed=true],[aria-selected=true],[aria-current=page],[data-state=on])]:bg-primary [&:is([aria-pressed=true],[aria-selected=true],[aria-current=page],[data-state=on])]:text-primary-foreground [&:is([aria-pressed=true],[aria-selected=true],[aria-current=page],[data-state=on])]:hover:bg-primary";

/**
 * Caixa de contrato (ex-consoleNote*): o "o que este número é, e o que ele
 * NÃO é" ao lado de um dado — fundo próprio para não se confundir com o
 * dado.
 */
export const notaDeContratoClass =
  "rounded-[var(--radius-inner)] border border-[var(--border)] bg-[var(--surface-2)] p-3";

export const notaDeContratoTituloClass =
  "block text-corpo text-[var(--text)]";

export const notaDeContratoTextoClass =
  "m-0 mt-1 max-w-4xl text-corpo leading-[1.5] text-[var(--muted)]";
